import { Show, createEffect, createSignal, on, onCleanup } from "solid-js";
import type { PlaySettings } from "../game/auto";
import { hintFor } from "../game/hints";
import type { Hint as HintLine } from "../game/hints";
import type { View } from "../game/view";
import { t } from "../strings";

// A newcomer's hints on the table (design 14 §9.4, game/hints): shown the first time their moment comes, kept in the
// account's settings as soon as shown, closed by a tap or by the move going on. Over the table, moving nothing.

export function Hint(props: { v: View; settings: PlaySettings; onSettings: (s: PlaySettings) => void; result: boolean }) {
  const [shown, setShown] = createSignal<(HintLine & { prompt: number }) | null>(null);
  const [bank, setBank] = createSignal(false);
  const promptId = () => {
    const p = props.v.prompt;
    return p && !p.sent ? p.prompt.promptId : 0;
  };

  // The bank's moment: the own move's time has run out (an untimed table has none).
  createEffect(
    on(promptId, () => {
      setBank(false);
      const p = props.v.prompt;
      if (!p || p.sent || !props.settings.hints || !p.prompt.baseMsLeft) return;
      const id = setTimeout(() => setBank(true), Math.max(0, p.prompt.baseMsLeft - (performance.now() - p.receivedAt)));
      onCleanup(() => clearTimeout(id));
    }),
  );

  createEffect(() => {
    const now = shown();
    // The moment has gone by: the move was made, or the result closed.
    if (now && (now.id === "result" ? !props.result : now.prompt !== promptId())) setShown(null);
    if (!props.settings.hints || shown()) return;
    const next = hintFor(props.v, props.settings.hints_seen, bank(), props.result, matchMedia("(pointer: coarse)").matches);
    if (!next) return;
    setShown({ ...next, prompt: promptId() });
    props.onSettings({ ...props.settings, hints_seen: [...props.settings.hints_seen, next.id] });
  });

  return (
    <Show when={shown()} keyed>
      {(h) => (
        <button type="button" class={`hint${h.id === "result" ? " hint-result" : ""}`} role="status" title={t.hints.close} onClick={() => setShown(null)}>
          {h.text}
        </button>
      )}
    </Show>
  );
}
